import React, { useState, useEffect, useContext } from "react";
import { XCard } from "../../index";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Pagination,
  PaginationItem,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SyncIcon from "@mui/icons-material/Sync";
import AiPositionIcon from "../../../shared/images/icons/ai-position.svg";
import { textXS } from "../../../shared/constants";
import axios from "axios";
import { SERVICEURL } from "../../../appconfig";
import {
  useAiSuggestedPositions,
  AiPosition,
} from "../../../hooks/candidate/useAiSuggestedPositions";
import { ThemeContext } from "../../../context/ThemeContextProvider";
import {
  getCardAddButtonSx,
  getWiserTheme,
} from "../../../shared/theme/wiserThemeHelpers";

interface AiSuggestedPositionsCardProps {
  talentId?: string;
  memberId?: string;
}

const DISPLAY_PAGE_SIZE = 10; // Ekranda kaç data göstereceğimiz

interface AiScoreCircleProps {
  score: number;
}

const AiScoreCircle: React.FC<AiScoreCircleProps> = ({ score }) => {
  const size = 35;
  const strokeWidth = 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const fillRatio = Math.min(Math.max(score, 0), 100) / 100;
  const dashOffset = circumference * (1 - fillRatio);
  const gradientId = `ai-score-grad-${score}`;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="5.62%" stopColor="#1968B0" />
            <stop offset="95.62%" stopColor="#56DB53" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
        />
      </svg>
      <Typography
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...textXS(700, "#1968B0"),
        }}
      >
        {score}
      </Typography>
    </Box>
  );
};

const AiSuggestedPositionsCard: React.FC<AiSuggestedPositionsCardProps> = ({
  talentId,
  memberId,
}) => {
  const { darkModeActive } = useContext(ThemeContext);
  const t = getWiserTheme(darkModeActive);
  const [page, setPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [aiHookEnabled, setAiHookEnabled] = useState(false);
  const [savedPositions, setSavedPositions] = useState<AiPosition[] | null>(
    null,
  );
  const [loadingSaved, setLoadingSaved] = useState(false);

  const {
    positions: hookPositions,
    totalCount: hookTotalCount,
    loading: hookLoading,
    resetCache,
  } = useAiSuggestedPositions(talentId, isExpanded && aiHookEnabled);

  const positions = savedPositions ?? hookPositions;
  const totalCount = savedPositions ? savedPositions.length : hookTotalCount;
  const loading = loadingSaved || hookLoading;

  // Sayfa numarasını talentId değişince sıfırla
  useEffect(() => {
    setPage(1);
  }, [talentId]);

  // AI hook'tan gelen veriyi DB'ye kaydet (sadece savedPositions yoksa, yani AI yolundan geliyorsa)
  useEffect(() => {
    if (!memberId || hookPositions.length === 0 || savedPositions !== null)
      return;

    const saveToDb = async () => {
      try {
        const token = localStorage.getItem("token");
        const requestData = {
          items: hookPositions.map((p) => ({
            positionId: p.positionId,
            aiMatchScore: p.matchScore,
            aiJustification: p.justification || "",
            aiExplanation: p.explanation || "",
          })),
        };
        await axios.post(
          `${SERVICEURL}api/StateInfo/member/${memberId}/SuggestedPositions`,
          requestData,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } catch (error) {
        console.error("AI pozisyonları kaydedilemedi:", error);
      }
    };

    saveToDb();
  }, [hookPositions, memberId, savedPositions]);

  // Kart açıldığında önce DB kontrolü yap
  useEffect(() => {
    if (!isExpanded || !memberId) {
      if (isExpanded && !memberId) setAiHookEnabled(true);
      return;
    }

    const checkSaved = async () => {
      try {
        setLoadingSaved(true);
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${SERVICEURL}api/StateInfo/member/${memberId}/SuggestedPositions`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const code = response.data.code;
        const responseData = response.data.data;

        if (code === 200 && responseData && responseData.length > 0) {
          const mapped: AiPosition[] = responseData.map((item: any) => ({
            positionId: item.positionId,
            positionName: item.positionName || "",
            workingModel: item.workingModel || "",
            client: item.client || "",
            techGroupNames: item.techGroupNames || [],
            matchScore: item.aiMatchScore || 0,
            justification: item.aiJustification || "",
            explanation: item.aiExplanation || "",
            team: item.team || "",
          }));
          setSavedPositions(mapped);
        } else {
          // DB'de kayıt yok, AI hook'unu aç
          setAiHookEnabled(true);
        }
      } catch {
        setAiHookEnabled(true);
      } finally {
        setLoadingSaved(false);
      }
    };

    checkSaved();
  }, [isExpanded, memberId]);

  const handleSync = async () => {
    if (!memberId) return;

    try {
      setSyncing(true);
      const token = localStorage.getItem("token");
      await axios.post(
        `${SERVICEURL}api/AICandidateSync/SyncCandidate/${memberId}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Cache'i temizle
      resetCache();
      setSavedPositions(null);
      setAiHookEnabled(true);
      // Sync başarılı, sayfayı yeniden yükle veya bildiri göster
      window.location.reload();
    } catch (error) {
      console.error("AI senkronizasyon hatası:", error);
      alert("Senkronizasyon sırasında bir hata oluştu.");
    } finally {
      setSyncing(false);
    }
  };

  const totalPages = Math.ceil(totalCount / DISPLAY_PAGE_SIZE);

  // Client-side pagination: Mevcut sayfaya göre 10'luk dilimi al
  const startIndex = (page - 1) * DISPLAY_PAGE_SIZE;
  const paginatedData = positions.slice(
    startIndex,
    startIndex + DISPLAY_PAGE_SIZE,
  );

  return (
    <XCard
      style="aiWhite"
      titleStyle="aiGradient"
      icon={<img src={AiPositionIcon} alt="ai-position" />}
      title="AI Önerilen Pozisyonlar"
      expandable
      onExpandChange={(expanded: boolean) => setIsExpanded(expanded)}
      // defaultExpanded
      headerAction={
        (talentId === null || talentId === "") && memberId ? (
          <Button
            size="small"
            startIcon={
              syncing ? (
                <CircularProgress size={14} sx={{ color: t.textPrimary }} />
              ) : (
                <SyncIcon fontSize="small" />
              )
            }
            onClick={handleSync}
            disabled={syncing}
            sx={{
              ...getCardAddButtonSx(darkModeActive),
              borderRadius: "6px",
              px: 1.5,
              py: 0.5,
              "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
            }}
          >
            {syncing ? "Senkronize ediliyor..." : "AI Senkronizasyon"}
          </Button>
        ) : null
      }
    >
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                "Pozisyon",
                "Çalışma Yöntemi",
                "Şirket",
                "Teknik Grup",
                "AI Score",
                // "İşlemler",
              ].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: darkModeActive ? "#FFFFFF" : "#6b7280",
                    borderBottom: `1px solid ${t.borderStrong}`,
                    py: 1,
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {talentId === undefined ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={32} sx={{ color: "#1968B0" }} />
                </TableCell>
              </TableRow>
            ) : loading ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={32} sx={{ color: "#1968B0" }} />
                </TableCell>
              </TableRow>
            ) : !talentId ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                  <Typography sx={{ fontSize: "13px", color: t.textMuted }}>
                    Veri bulunamadı. AI Senkronizasyon yapmanız gerekmektedir.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                  <Typography sx={{ fontSize: "13px", color: t.textMuted }}>
                    Henüz pozisyon önerisi bulunmamaktadır.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row) => (
                <TableRow
                  key={row.positionId}
                  sx={{
                    "&:hover": {
                      backgroundColor: darkModeActive
                        ? "rgba(255,255,255,0.05)"
                        : "#f9fafb",
                    },
                    "& td": { borderBottom: "none", py: 0.8 },
                  }}
                >
                  <TableCell
                    sx={{ fontSize: "13px", color: t.textPrimary, py: 1 }}
                  >
                    {row.positionName}
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    {row.workingModel && (
                      <Chip
                        label={row.workingModel}
                        size="small"
                        sx={{
                          backgroundColor:
                            row.workingModel === "Remote"
                              ? "#EAE6FF"
                              : "#3BACD91F",
                          height: "16px",
                          borderRadius: "3px",
                          ...textXS(600, "#42526E"),
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", color: t.textPrimary, py: 1 }}>
                    {row.client}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", color: t.textPrimary, py: 1 }}>
                    {row.techGroupNames?.join(", ") || ""}
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <AiScoreCircle score={row.matchScore} />
                  </TableCell>
                  {/* <TableCell sx={{ py: 1 }}>
                    <IconButton size="small" sx={{ color: "#ef4444" }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell> */}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          pt: 1,
          borderTop: `1px solid ${t.borderStrong}`,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontSize: "12px", color: t.textMuted }}>
          Sayfa {totalPages} Git
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, val) => setPage(val)}
          size="small"
          siblingCount={2}
          renderItem={(item) => (
            <PaginationItem
              {...item}
              sx={{
                fontSize: "12px",
                minWidth: "28px",
                height: "28px",
                "&.Mui-selected": {
                  backgroundColor: darkModeActive ? t.accent : "#1e3a5f",
                  color: "#fff",
                },
              }}
            />
          )}
        />
        <Typography sx={{ fontSize: "12px", color: t.textMuted }}>
          {Math.min((page - 1) * DISPLAY_PAGE_SIZE + 1, totalCount)}–
          {Math.min(page * DISPLAY_PAGE_SIZE, totalCount)} / {totalCount}
        </Typography>
      </Box>
    </XCard>
  );
};

export default AiSuggestedPositionsCard;
